/**
 * ClaimShield Portal - Client Portal Entry Point
 *
 * Serves: portal.claimshieldpro.com
 * Audience: Client users (trucking companies, OSHA clients)
 */
import { createApp } from 'vue'
import { createPinia } from 'pinia'

// PrimeVue
import PrimeVue from 'primevue/config'
import Aura from '@primevue/themes/aura'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import Tooltip from 'primevue/tooltip'

// PrimeVue components
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Card from 'primevue/card'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Dialog from 'primevue/dialog'
import Toast from 'primevue/toast'
import Menu from 'primevue/menu'
import Avatar from 'primevue/avatar'
import Badge from 'primevue/badge'
import Tag from 'primevue/tag'
import ProgressSpinner from 'primevue/progressspinner'
import Skeleton from 'primevue/skeleton'
import Dropdown from 'primevue/dropdown'
import DatePicker from 'primevue/datepicker'
import FileUpload from 'primevue/fileupload'
import Select from 'primevue/select'

// App and router
import App from './App.vue'
import { router } from './router'
import { installErrorCapture } from '@claimshield/shared'

// Styles
import '@claimshield/shared/styles'
import 'primeicons/primeicons.css'

// Create Vue app
const app = createApp(App)

// Pinia state management
const pinia = createPinia()
app.use(pinia)

// PrimeVue with Aura theme
app.use(PrimeVue, {
  theme: {
    preset: Aura,
    options: {
      prefix: 'p',
      darkModeSelector: '.dark-mode',
      cssLayer: {
        name: 'primevue',
        order: 'tailwind-base, primevue, tailwind-utilities',
      },
    },
  },
  ripple: true,
})

// PrimeVue services
app.use(ToastService)
app.use(ConfirmationService)

// PrimeVue directive
app.directive('tooltip', Tooltip)

// Register PrimeVue components globally
app.component('PButton', Button)
app.component('PInputText', InputText)
app.component('PPassword', Password)
app.component('PCard', Card)
app.component('PDataTable', DataTable)
app.component('PColumn', Column)
app.component('PDialog', Dialog)
app.component('PToast', Toast)
app.component('PMenu', Menu)
app.component('PAvatar', Avatar)
app.component('PBadge', Badge)
app.component('PTag', Tag)
app.component('PProgressSpinner', ProgressSpinner)
app.component('PSkeleton', Skeleton)
app.component('PDropdown', Dropdown)
app.component('PDatePicker', DatePicker)
app.component('PCalendar', DatePicker)  // PrimeVue 4 renamed Calendar to DatePicker
app.component('PFileUpload', FileUpload)
app.component('PSelect', Select)

// Router
app.use(router)

// Global JS error capture — must run after Pinia is registered so the
// capture handler can read the auth store when reporting.
installErrorCapture(app)

// Mount
app.mount('#app')
